import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic2, Trash2, Plus, Sun, Moon, Monitor, Bell, Lock, Download, Loader2 } from "lucide-react";
import useTheme from "../../components/aura/useTheme";
import { SectionHeader } from "../components/Shared";
import { supabase } from "../../lib/supabase";
import ModeSelector, { SessionMode } from "../components/ModeSelector";

const Settings = () => {
  const { mode, effective, setTheme } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [userId, setUserId] = useState(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [targetLang, setTargetLang] = useState("");
  const [nativeLang, setNativeLang] = useState("");
  const [voiceDone] = useState(false);
  
  // Curriculum state
  const [selectedMode, setSelectedMode] = useState<SessionMode>('free_conversation');

  useEffect(() => {
    async function loadProfile() {
      const { data: authData } = await supabase.auth.getUser();
      const uid = authData.user?.id;
      if (!uid) {
        setLoading(false);
        return; // Handle not logged in case properly in real app
      }
      setUserId(uid);

      const [{ data: profile }, { data: currState }] = await Promise.all([
        supabase.from('users').select('*').eq('id', uid).maybeSingle(),
        supabase.from('user_curriculum_state').select('active_mode_slug').eq('user_id', uid).maybeSingle(),
      ]);

      if (profile) {
        setName(profile.display_name || "");
        setEmail(authData.user.email || "");
        setTargetLang(profile.target_language || "");
        setNativeLang(profile.native_language || "");
      }

      if (currState?.active_mode_slug) {
        setSelectedMode(currState.active_mode_slug as SessionMode);
      } else {
        const local = localStorage.getItem('aura_mode');
        if (local) setSelectedMode(local as SessionMode);
      }

      setLoading(false);
    }
    loadProfile();
  }, []);

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    setSaved(false);

    try {
      // 1. Save profile
      const { error: profileErr } = await supabase
        .from('users')
        .update({
          display_name: name,
          target_language: targetLang || undefined,
          native_language: nativeLang || undefined,
        })
        .eq('id', userId);

      if (profileErr) throw profileErr;

      // 2. Upsert curriculum state (active mode)
      const { error: modeErr } = await supabase
        .from('user_curriculum_state')
        .upsert(
          {
            user_id: userId,
            active_mode_slug: selectedMode,
          },
          { onConflict: 'user_id' }
        );

      if (modeErr) throw modeErr;

      // Sync to localStorage
      localStorage.setItem('aura_mode', selectedMode);

      window.dispatchEvent(new Event('aura_profile_updated'));

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="animate-spin text-aura-cyan" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="page-settings">
      {/* Profile */}
      <div className="rounded-3xl glass edge-glow p-6 sm:p-8" data-testid="settings-profile">
        <SectionHeader action={null} eyebrow="Profile" title="How AURA addresses you." />
        <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-center">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-aura-cyan/40 to-aura-violet/40 grid place-items-center font-display text-[32px] text-ink">{name.charAt(0) || "U"}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full bg-panel/[0.02] border border-panel/10 rounded-xl px-3 py-2.5 text-[14px] text-ink focus:outline-none focus:border-aura-cyan/60" data-testid="settings-name" />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">Email</span>
              <input disabled value={email} className="mt-1 w-full bg-panel/[0.02] border border-panel/10 rounded-xl px-3 py-2.5 text-[14px] text-ink/50 cursor-not-allowed focus:outline-none" data-testid="settings-email" />
            </label>
          </div>
        </div>
      </div>

      {/* Curriculum Mode */}
      <div className="rounded-3xl glass hairline p-6 sm:p-8" data-testid="settings-curriculum">
        <SectionHeader action={null} eyebrow="Curriculum Mode" title="AURA's Cognitive Engine will steer every session according to this mode." />
        <div className="mt-4">
           <ModeSelector selected={selectedMode} onChange={setSelectedMode} />
        </div>
      </div>

      {/* Voice print */}
      <div className="rounded-3xl glass hairline p-6 sm:p-8 flex flex-wrap items-center gap-4 justify-between" data-testid="settings-voice">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-2xl bg-aura-cyan/10 border border-aura-cyan/30 grid place-items-center text-aura-cyan"><Mic2 size={18} /></span>
          <div>
            <div className="font-display text-[18px] text-ink">Voice print</div>
            <div className="text-whisper text-[13px]">{voiceDone ? "Captured · helps AURA recognise your tone, pace, and accent." : "Record once · she'll listen better forever."}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost text-[10px] py-2 px-3"><Mic2 size={11} /> re-record</button>
          <button className="btn-ghost text-[10px] py-2 px-3 text-red-300 border-red-400/30"><Trash2 size={11} /> delete</button>
        </div>
      </div>

      {/* Languages */}
      <div className="rounded-3xl glass hairline p-6 sm:p-8" data-testid="settings-languages">
        <SectionHeader action={null} eyebrow="Languages" title="One memory · many voices." />
        <div className="grid sm:grid-cols-2 gap-3">
          {targetLang && (
            <div className="hairline rounded-2xl p-4 flex items-center justify-between gap-3">
              <div>
                <div className="font-display text-[16px] text-ink">{targetLang}</div>
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted mt-1">Learning · primary</div>
              </div>
            </div>
          )}
          {nativeLang && (
            <div className="hairline rounded-2xl p-4 flex items-center justify-between gap-3">
              <div>
                <div className="font-display text-[16px] text-ink">{nativeLang}</div>
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted mt-1">Native</div>
              </div>
            </div>
          )}
          <button className="hairline rounded-2xl p-4 flex items-center justify-center gap-2 text-whisper hover:text-ink hover:border-aura-cyan/40 transition-colors" data-testid="settings-add-language">
            <Plus size={14} /> add language
          </button>
        </div>
      </div>

      {/* Theme */}
      <div className="rounded-3xl glass hairline p-6 sm:p-8" data-testid="settings-theme">
        <SectionHeader action={null} eyebrow="Appearance" title="Light, dark, or system." />
        <div className="grid grid-cols-3 gap-3 max-w-md">
          {[
            { id: "light", label: "Light", icon: Sun },
            { id: "system", label: "System", icon: Monitor },
            { id: "dark", label: "Dark", icon: Moon },
          ].map((t) => (
            <button key={t.id} onClick={() => setTheme(t.id)}
              className={`hairline rounded-2xl py-4 flex flex-col items-center gap-2 transition-colors ${
                mode === t.id ? "border-aura-cyan/40 bg-aura-cyan/5" : "hover:border-aura-cyan/30"
              }`}
              data-testid={`settings-theme-${t.id}`}
            >
              <t.icon size={18} className={mode === t.id ? "text-aura-cyan" : "text-whisper"} />
              <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase">{t.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">Currently · {effective}</div>
      </div>

      {/* Notifications */}
      <div className="rounded-3xl glass hairline p-6 sm:p-8" data-testid="settings-notifications">
        <SectionHeader action={null} eyebrow="Notifications" title="Stay quiet, stay sharp." />
        <div className="space-y-3">
          {[
            "Daily session reminder",
            "Weekly memory digest",
            "Goal milestones",
            "Breakthrough alerts",
          ].map((label, i) => (
            <label key={label} className="flex items-center justify-between gap-3 hairline rounded-xl p-4 cursor-pointer">
              <div className="flex items-center gap-3">
                <Bell size={14} className="text-whisper" />
                <span className="text-[14px] text-ink/90">{label}</span>
              </div>
              <input type="checkbox" defaultChecked={i < 2} className="theme-swap appearance-none cursor-pointer" />
            </label>
          ))}
        </div>
      </div>

      {/* Privacy & data */}
      <div className="rounded-3xl glass hairline p-6 sm:p-8" data-testid="settings-privacy">
        <SectionHeader action={null} eyebrow="Privacy & data" title="Your memory stays yours." />
        <div className="grid sm:grid-cols-2 gap-3">
          <button className="hairline rounded-2xl p-4 flex items-center gap-3 hover:border-aura-cyan/40">
            <Download size={14} className="text-aura-cyan" />
            <div className="text-left">
              <div className="font-display text-[14.5px] text-ink">Export your memory</div>
              <div className="text-whisper text-[12.5px]">Download every node as JSON.</div>
            </div>
          </button>
          <button className="hairline rounded-2xl p-4 flex items-center gap-3 hover:border-aura-cyan/40">
            <Lock size={14} className="text-aura-cyan" />
            <div className="text-left">
              <div className="font-display text-[14.5px] text-ink">Privacy policy</div>
              <div className="text-whisper text-[12.5px]">What we store · why · for how long.</div>
            </div>
          </button>
        </div>
        <div className="mt-4 hairline rounded-2xl p-4 border-red-400/20">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-red-300">danger zone</div>
          <div className="mt-1 flex items-center justify-between">
            <div className="text-whisper text-[13px]">Delete account & wipe all memory permanently.</div>
            <button className="btn-ghost text-[10px] py-2 px-3 text-red-300 border-red-400/30">delete</button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-4">
        <AnimatePresence>
          {saved && (
            <motion.span 
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              className="text-aura-cyan text-[13px] mr-4"
            >
              Changes saved successfully.
            </motion.span>
          )}
        </AnimatePresence>
        <button className="btn-ghost">cancel</button>
        <motion.button 
          onClick={handleSave} 
          disabled={saving}
          whileHover={{ scale: 1.02 }} 
          whileTap={{ scale: 0.98 }} 
          className={`btn-primary ${saved ? 'bg-aura-cyan/20 border-aura-cyan/50 text-aura-cyan' : ''}`} 
          data-testid="settings-save"
        >
          {saving ? <Loader2 className="animate-spin inline-block mr-2" size={16} /> : null}
          {saved ? "Saved" : "save changes"}
        </motion.button>
      </div>
    </div>
  );
};

export default Settings;
